<script lang="ts">
  import { createEventDispatcher } from 'svelte';

  export let wrongGuesses: number = 0;
  export let maxWrong: number = 6;

  const dispatch = createEventDispatcher();

  // SVG viewBox: 0 0 200 240
  // Parts are drawn in order: base, pole, top, rope, head, body, left arm, right arm, left leg, right leg
  const parts = [
    { name: 'base', label: 'Stand base' },
    { name: 'pole', label: 'Pole' },
    { name: 'top', label: 'Top bar' },
    { name: 'rope', label: 'Rope' },
    { name: 'head', label: 'Head' },
    { name: 'body', label: 'Body' },
    { name: 'leftArm', label: 'Left arm' },
    { name: 'rightArm', label: 'Right arm' },
    { name: 'leftLeg', label: 'Left leg' },
    { name: 'rightLeg', label: 'Right leg' },
  ];

  // Map wrongGuesses to visible parts
  // 0-3: gallows, 4+: body parts
  $: visibleParts = getVisibleParts(wrongGuesses, maxWrong);

  function getVisibleParts(wrong: number, max: number): number {
    // Scale wrong guesses to 10 parts (gallows + body)
    const totalParts = 10;
    const ratio = wrong / max;
    return Math.min(totalParts, Math.ceil(ratio * totalParts));
  }

  $: animateKey = wrongGuesses;
</script>

<svg
  class="hangman-figure"
  viewBox="0 0 200 240"
  xmlns="http://www.w3.org/2000/svg"
  aria-label="Hangman figure showing {wrongGuesses} wrong guesses out of {maxWrong}"
>
  <!-- Base (horizontal ground) -->
  <g class:visible={visibleParts >= 1} class="part part-base">
    <line x1="20" y1="230" x2="180" y2="230" stroke="var(--figure-stroke)" stroke-width="4" stroke-linecap="round"/>
    <line x1="20" y1="230" x2="20" y2="225" stroke="var(--figure-stroke)" stroke-width="3" stroke-linecap="round"/>
    <line x1="180" y1="230" x2="180" y2="225" stroke="var(--figure-stroke)" stroke-width="3" stroke-linecap="round"/>
  </g>

  <!-- Vertical pole -->
  <g class:visible={visibleParts >= 2} class="part part-pole">
    <line x1="40" y1="230" x2="40" y2="40" stroke="var(--figure-stroke)" stroke-width="4" stroke-linecap="round"/>
  </g>

  <!-- Top bar -->
  <g class:visible={visibleParts >= 3} class="part part-top">
    <line x1="40" y1="40" x2="140" y2="40" stroke="var(--figure-stroke)" stroke-width="4" stroke-linecap="round"/>
  </g>

  <!-- Rope / diagonal support -->
  <g class:visible={visibleParts >= 4} class="part part-rope">
    <line x1="140" y1="40" x2="140" y2="65" stroke="var(--figure-stroke)" stroke-width="3" stroke-linecap="round"/>
    <line x1="40" y1="40" x2="20" y2="60" stroke="var(--figure-stroke)" stroke-width="3" stroke-linecap="round"/>
  </g>

  <!-- Head -->
  <g class:visible={visibleParts >= 5} class="part part-head">
    <circle cx="140" cy="82" r="16" stroke="var(--figure-stroke)" stroke-width="3" fill="none"/>
    <!-- Face details -->
    <line x1="134" y1="78" x2="138" y2="82" stroke="var(--figure-stroke)" stroke-width="2" stroke-linecap="round"/>
    <line x1="142" y1="78" x2="146" y2="82" stroke="var(--figure-stroke)" stroke-width="2" stroke-linecap="round"/>
    <line x1="136" y1="88" x2="144" y2="88" stroke="var(--figure-stroke)" stroke-width="2" stroke-linecap="round"/>
  </g>

  <!-- Body -->
  <g class:visible={visibleParts >= 6} class="part part-body">
    <line x1="140" y1="98" x2="140" y2="155" stroke="var(--figure-stroke)" stroke-width="3" stroke-linecap="round"/>
  </g>

  <!-- Left arm -->
  <g class:visible={visibleParts >= 7} class="part part-left-arm">
    <line x1="140" y1="115" x2="110" y2="135" stroke="var(--figure-stroke)" stroke-width="3" stroke-linecap="round"/>
  </g>

  <!-- Right arm -->
  <g class:visible={visibleParts >= 8} class="part part-right-arm">
    <line x1="140" y1="115" x2="170" y2="135" stroke="var(--figure-stroke)" stroke-width="3" stroke-linecap="round"/>
  </g>

  <!-- Left leg -->
  <g class:visible={visibleParts >= 9} class="part part-left-leg">
    <line x1="140" y1="155" x2="115" y2="195" stroke="var(--figure-stroke)" stroke-width="3" stroke-linecap="round"/>
  </g>

  <!-- Right leg -->
  <g class:visible={visibleParts >= 10} class="part part-right-leg">
    <line x1="140" y1="155" x2="165" y2="195" stroke="var(--figure-stroke)" stroke-width="3" stroke-linecap="round"/>
  </g>
</svg>

<style>
  .hangman-figure {
    width: 100%;
    max-width: 200px;
    height: auto;
    display: block;
    margin: 0 auto;
  }

  @media (min-width: 769px) {
    .hangman-figure {
      max-width: 260px;
    }
  }

  .part {
    opacity: 0;
    transition: opacity 0.4s ease, stroke-dashoffset 0.5s ease;
  }

  .part.visible {
    opacity: 1;
  }

  :global(.hangman-figure) {
    --figure-stroke: var(--hangman-stroke, #e74c3c);
  }
</style>
